import { Component, DestroyRef, OnInit } from '@angular/core';
import { UtilService } from '../../../services/util.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
  API,
  CullDTO,
  CullWithAtomsDTO,
  GeoCullDTO,
  Pagination,
  PlaybackScheduleFullDTO,
  PlayerCullDTO,
  ScreenScope,
  StateSpaceCullDTO,
  TimeCullDTO,
} from '../../../services/api/api';
import { ActivatedRoute } from '@angular/router';
import { ErrorService } from '../../../services/error.service';
import { NavigationService } from '@ngx-seatback/utils';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { PermissionService } from '../../../services/permission.service';
import { SbWizardComponent, SbWizardTabComponent } from '@ngx-seatback/layout';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-page-schedule-edit',
  templateUrl: './schedule-edit.component.html',
  styleUrls: ['./schedule-edit.component.scss'],
  standalone: false,
})
export class ScheduleEditComponent implements OnInit {
  protected readonly ScreenScope = ScreenScope;
  loading = true;
  scheduleId: number;
  schedule: PlaybackScheduleFullDTO;
  schedules: PlaybackScheduleFullDTO[];
  previewSchedule: PlaybackScheduleFullDTO;
  wizardState = 'Map';
  wizardPreviewState = 'locationMap';

  form: UntypedFormGroup;

  precondition: PlaybackScheduleFullDTO;
  preconditions: PlaybackScheduleFullDTO[];
  pickedPosition: PlaybackScheduleFullDTO;
  pagination: BehaviorSubject<Pagination>;

  constructor(
    public util: UtilService,
    private activatedRoute: ActivatedRoute,
    private api: API,
    private errorHandler: ErrorService,
    private navService: NavigationService,
    public modal: ModalService,
    public translate: TranslateService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.pagination = new BehaviorSubject<Pagination>({
      page: 1,
      count: 500,
      pages: -1,
      resultcount: -1,
    });
    this.precondition = null;
    this.pickedPosition = null;
    this.form = new UntypedFormGroup({
      Title: new UntypedFormControl('', [Validators.required]),
    });
  }

  get Title() {
    return this.form.get('Title');
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.scheduleId = params.scheduleID;
      this.api
        .ss_nm_PlaybackSchedule_retrieveAll({}, this.pagination)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (schedules: PlaybackScheduleFullDTO[]) => {
            this.pickedPosition = this.scheduleId ? schedules.find(x => x.id?.toString() === this.scheduleId?.toString()) : null;
            this.schedules = this.scheduleId ? schedules.filter(x => x?.id?.toString() !== this.scheduleId?.toString()) : schedules;
            this.refreshFromServer();
            this.registerFormChangesListener();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    });
  }

  private registerFormChangesListener() {
    this.Title.valueChanges.subscribe(value => {
      if (this.schedule) {
        this.schedule.name = value;
      }
    });
  }

  updateForm() {
    if (this.schedule?.id) {
      this.Title.setValue(this.schedule.name);
    }
  }

  nextStep(wizard: SbWizardComponent, nextTab: SbWizardTabComponent) {
    wizard.selectTab(nextTab);
  }

  setCull(cull: CullDTO) {
    switch (cull.type) {
      case 'TimeCull':
        this.schedule.timeCull = cull as TimeCullDTO;
        if (!cull.id) {
          this.schedule.timeCull = null;
        }
        break;
      case 'StateSpaceCull':
        this.schedule.stateSpaceCull = cull as StateSpaceCullDTO;
        if (!cull.id) {
          this.schedule.stateSpaceCull = null;
        }
        break;
      case 'GeoCull':
        this.schedule.geoCull = cull as GeoCullDTO;
        if (!cull.id) {
          this.schedule.geoCull = null;
        }
        break;
      case 'PlayerCull':
        this.schedule.playerCull = cull as PlayerCullDTO;
        if (!cull.id) {
          this.schedule.playerCull = null;
        }
        break;
    }
  }

  sliceCull(e: any, type: string) {
    switch (type) {
      case 'TimeCull': {
        if (this.schedule.timeCull) {
          this.schedule.timeCull = null;
        }
        break;
      }
      case 'PlayerCull': {
        if (this.schedule.playerCull) {
          this.schedule.playerCull = null;
        }
        break;
      }
      case 'GeoCull': {
        if (this.schedule.geoCull) {
          this.schedule.geoCull = null;
        }
        break;
      }
    }
    this.changeWizardState('Map');
    e.stopPropagation();
  }

  refreshFromServer() {
    this.loading = true;
    if (!this.scheduleId) {
      this.schedule = new PlaybackScheduleFullDTO();
      this.loading = false;
      this.retrieveHierarchy();
    } else {
      this.api
        .ss_nm_PlaybackSchedule_retrieveById(this.scheduleId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: data => {
            this.schedule = data;
            this.precondition = this.schedule.precondition
              ? this.schedules.find(schedule => schedule.id === this.schedule.precondition.id)
              : null;
            this.updateForm();
            this.retrieveHierarchy();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  createAndUpdateSchedule() {
    this.loading = true;
    this.schedule.precondition = this.precondition;
    const getPreconditionPrio = this.preconditions.length > 0 ? this.preconditions[this.preconditions.length - 1].priority + 1 : 0;
    this.schedule.priority = this.pickedPosition ? this.pickedPosition.priority : getPreconditionPrio;
    if (!this.schedule.id) {
      this.api
        .ss_nm_PlaybackSchedule_createPlaybackSchedule(this.schedule)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: data => {
            this.schedule = data;
            this.navService.navigateToPoint(this.util.RoutingID.scheduleDetails, [{ id: 'scheduleID', value: data.id }]);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.api
        .ss_nm_PlaybackSchedule_updateById(this.schedule)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: data => {
            this.schedule = data;
            this.navService.navigateToPoint(this.util.RoutingID.scheduleDetails, [{ id: 'scheduleID', value: data.id }]);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  selectFilter(cull: CullDTO) {
    this.setCull(cull);
    this.changeWizardState('Map');
  }

  filterSubmitSuccess(cull: CullWithAtomsDTO) {
    if (!cull) {
      this.changeWizardState('Map');
    } else {
      switch (cull.type) {
        case 'TimeCull':
          this.selectFilter(cull as TimeCullDTO);
          break;
        case 'PlayerCull':
          this.selectFilter(cull as PlayerCullDTO);
          break;
      }
    }
  }

  changeWizardState(state: string) {
    this.wizardState = state;
  }

  changeWizardPreviewState(state: string) {
    this.wizardPreviewState = state;
  }

  updatePosition($event: PlaybackScheduleFullDTO) {
    this.pickedPosition = $event;
  }

  updatePrecondition($event: PlaybackScheduleFullDTO) {
    this.precondition = $event;
    this.retrieveHierarchy();
  }

  private retrieveHierarchy() {
    this.api
      .ss_nm_PlaybackSchedule_retrieveHierarchy(
        {
          precondition: this.precondition?.id ? this.precondition.id : null,
        },
        this.pagination
      )
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (preconditions: PlaybackScheduleFullDTO[]) => {
          const helper = preconditions.filter(x => x.id?.toString() === this.scheduleId?.toString())[0];
          this.pickedPosition = helper ? helper : null;
          this.preconditions = preconditions;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }
}
